import { useEffect, useMemo, useState } from "react";
import { formatCurrency } from "../views/Obras/obraFormat";

/**
 * Seleção manual de linhas com total dinâmico (à Excel). A seleção vive dentro da listagem
 * atual: mudar os filtros retira automaticamente as linhas que saíram do resultado — marcar
 * tudo e filtrar a seguir não deixa uma soma fantasma no rodapé. Os valores somados são sempre
 * os mostrados nas linhas (a fatia da obra, se há filtro de obra), recalculados a cada render.
 * Para somar itens que nenhum filtro único apanha, limpa-se o filtro e marca-se na lista
 * completa.
 */

export interface SelectedInvoiceLine {
  id: number;
  /** s/IVA tal como está na linha. */
  base: number;
  /** c/IVA tal como está na linha. */
  total: number;
}

export interface InvoiceSelection {
  isSelected: (id: number) => boolean;
  toggle: (id: number) => void;
  /** Marca todas as visíveis; se já estiverem todas marcadas, desmarca-as (checkbox do cabeçalho). */
  toggleAll: () => void;
  clear: () => void;
  count: number;
  baseTotal: number;
  grandTotal: number;
  /** Ids das linhas marcadas, pela ordem da listagem (ex.: para exportar só a seleção). */
  ids: number[];
  allSelected: boolean;
  anySelected: boolean;
}

export function useInvoiceSelection(
  lines: SelectedInvoiceLine[],
): InvoiceSelection {
  const [selected, setSelected] = useState<Set<number>>(() => new Set());

  // Assinatura estável das linhas visíveis, para o prune só correr quando a listagem muda.
  const idsKey = lines.map((line) => line.id).join(",");

  // Mudou a listagem (filtros/pesquisa/reload): as linhas que saíram deixam de estar marcadas.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(
        idsKey ? idsKey.split(",").map(Number) : [],
      );
      const next = new Set(
        Array.from(prev).filter((id) => visible.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [idsKey]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const allSelected =
    lines.length > 0 && lines.every((line) => selected.has(line.id));
  const anySelected = lines.some((line) => selected.has(line.id));

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      lines.forEach((line) => {
        if (allSelected) {
          next.delete(line.id);
        } else {
          next.add(line.id);
        }
      });
      return next;
    });

  const totals = useMemo(() => {
    let base = 0;
    let total = 0;
    const ids: number[] = [];
    lines.forEach((line) => {
      if (selected.has(line.id)) {
        base += line.base;
        total += line.total;
        ids.push(line.id);
      }
    });
    return { base, total, ids };
  }, [lines, selected]);

  return {
    isSelected: (id: number) => selected.has(id),
    toggle,
    toggleAll,
    clear: () => setSelected(new Set()),
    count: totals.ids.length,
    baseTotal: totals.base,
    grandTotal: totals.total,
    ids: totals.ids,
    allSelected,
    anySelected,
  };
}

/**
 * Linha de rodapé com o total da seleção, desenhada por cima da linha de totais do filtro. As
 * colunas dos valores alinham com as colunas s/IVA e c/IVA da tabela:
 * `labelColSpan` + 2 células de valor + `trailingColSpan` = nº de colunas da tabela.
 */
export function SelectionTotalsRow({
  selection,
  labelColSpan,
  trailingColSpan,
}: {
  selection: InvoiceSelection;
  labelColSpan: number;
  trailingColSpan: number;
}) {
  if (selection.count === 0) return null;

  return (
    <tr className="border-t border-border" style={{ backgroundColor: "var(--accent)" }}>
      <td className="px-3 py-2" colSpan={labelColSpan}>
        <span className="inline-flex items-center gap-2">
          <span
            style={{
              color: "var(--primary)",
              fontWeight: "var(--font-weight-medium)",
            }}
          >
            {selection.count === 1
              ? "1 linha selecionada"
              : `${selection.count} linhas selecionadas`}
          </span>
          <button
            type="button"
            onClick={selection.clear}
            className="text-xs px-2 py-0.5 border border-border rounded hover:bg-accent/30"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Limpar
          </button>
        </span>
      </td>
      <td
        className="px-3 py-2 text-right whitespace-nowrap"
        style={{ color: "var(--primary)" }}
      >
        {formatCurrency(selection.baseTotal)}
      </td>
      <td
        className="px-3 py-2 text-right whitespace-nowrap"
        style={{ color: "var(--primary)" }}
      >
        {formatCurrency(selection.grandTotal)}
      </td>
      <td className="px-3 py-2" colSpan={trailingColSpan} />
    </tr>
  );
}
